import React, { useRef, useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

interface PullToRefreshProps {
	children: React.ReactNode;
	onRefresh?: () => Promise<void> | void;
	thresholdPx?: number;
	enabled?: boolean;
}

// Lightweight pull-to-refresh for mobile webviews (Android)
// Uses touch events to detect a downward pull from top and triggers refresh
const PullToRefresh: React.FC<PullToRefreshProps> = ({
	children,
	onRefresh,
	thresholdPx = 70,
	enabled,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const startYRef = useRef<number | null>(null);
	const [pulled, setPulled] = useState(0);
	const [refreshing, setRefreshing] = useState(false);

	const isAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
	const isEnabled = enabled ?? isAndroid;

	useEffect(() => {
		if (!isEnabled) return;
		const el = containerRef.current;
		if (!el) return;

		const handleTouchStart = (e: TouchEvent) => {
			startYRef.current = null;
			if (window.scrollY > 0 || refreshing || e.touches.length !== 1) return;
			startYRef.current = e.touches[0].clientY;
			setPulled(0);
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (startYRef.current === null || refreshing || e.touches.length !== 1) return;
			const currentY = e.touches[0].clientY;
			const delta = currentY - startYRef.current;
			if (delta <= 0 || window.scrollY > 0) {
				startYRef.current = null;
				setPulled(0);
				return;
			}
			if (delta > 0) {
				e.preventDefault();
				setPulled(Math.min(delta, thresholdPx * 2));
			}
		};

		const handleTouchEnd = async () => {
			if (refreshing) return;
			if (pulled >= thresholdPx) {
				try {
					setRefreshing(true);
					if (onRefresh) {
						await onRefresh();
					} else {
						window.location.reload();
					}
				} finally {
					setRefreshing(false);
					setPulled(0);
				}
			} else {
				setPulled(0);
			}
			startYRef.current = null;
		};

		el.addEventListener('touchstart', handleTouchStart, { passive: false });
		el.addEventListener('touchmove', handleTouchMove, { passive: false });
		const handleTouchCancel = () => { startYRef.current = null; setPulled(0); };
			el.addEventListener('touchend', handleTouchEnd);
			el.addEventListener('touchcancel', handleTouchCancel);

		return () => {
			el.removeEventListener('touchstart', handleTouchStart as any);
			el.removeEventListener('touchmove', handleTouchMove as any);
			el.removeEventListener('touchend', handleTouchEnd as any);
			el.removeEventListener('touchcancel', handleTouchCancel);
		};
	}, [isEnabled, thresholdPx, pulled, refreshing, onRefresh]);

	if (!isEnabled) return <>{children}</>;

	return (
		<div ref={containerRef} style={{ touchAction: 'pan-y pinch-zoom' }}>
			<div
				style={{
					height: pulled > 0 || refreshing ? 56 : 0,
					transition: refreshing ? 'height 120ms ease' : 'height 160ms ease',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: '#2563eb',
					fontSize: 12,
				}}
			>
				{refreshing ? 'Refreshing…' : pulled >= thresholdPx ? 'Release to refresh' : 'Pull to refresh'}
			</div>
			<div
				style={{
					transform: pulled > 0 ? `translateY(${Math.min(pulled, thresholdPx)}px)` : 'none',
					transition: refreshing ? 'transform 120ms ease' : 'transform 160ms ease',
				}}
			>
				{children}
			</div>
		</div>
	);
};

export default PullToRefresh;



